import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, Link2, Trash2, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, StatusBadge } from "@/components/common";
import { AMENITIES } from "@/pages/Search";
import { useLang } from "@/lib/i18n";
import { api, eur, errMsg, photoUrl } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { title: "", title_en: "", description: "", description_en: "", city: "", zone: "", address: "", property_type: "apartment", bedrooms: 1, bathrooms: 1,
  max_guests: 2, size_sqm: 50, price_month: 1000, min_months: 1, available_from: "", amenities: ["wifi"], extras: [] };
const TYPES = ["apartment", "studio", "loft", "house", "room", "villa"];
const Card = ({ title, children }) => (
  <section className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6"><h2 className="text-lg font-bold text-[#0B192C] mb-5">{title}</h2>{children}</section>
);

function Photos({ listing, setListing }) {
  const { t } = useLang();
  const fileRef = useRef();
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState("");
  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy("upload");
    try { const { data } = await api.post(`/my/listings/${listing.listing_id}/photos`, fd); setListing(data); toast.success("OK"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const doImport = async () => {
    const list = urls.split(/\s+/).filter(Boolean);
    if (!list.length) return;
    setBusy("import");
    try { const { data } = await api.post(`/my/listings/${listing.listing_id}/import-photos`, { urls: list }); setListing(data); setUrls(""); toast.success(t("imported", { n: data.imported })); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const remove = async (i) => { const { data } = await api.delete(`/my/listings/${listing.listing_id}/photos/${i}`); setListing(data); };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="editor-photos">
        {listing.photos.map((p, i) => (
          <div key={i} className="relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
            <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
            {p.source === "hwsmart" && <span className="absolute left-1.5 top-1.5 rounded-full bg-[#142B5C] px-2 py-0.5 text-[10px] font-bold text-white">HWSmart</span>}
            <button onClick={() => remove(i)} className="absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-full bg-white/95 text-red-600" data-testid={`remove-photo-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
      <button type="button" onClick={() => fileRef.current.click()} disabled={!!busy} className="h-12 w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 font-semibold text-[#142B5C] hover:border-[#FF6B00]" data-testid="upload-photos-btn">
        {busy === "upload" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}{t("upload_photos")}
      </button>
      <div className="rounded-xl bg-slate-50 p-4 space-y-3">
        <p className="flex items-center gap-2 text-sm font-bold text-[#142B5C]"><Link2 className="h-4 w-4 text-[#FF6B00]" />{t("hw_import")}</p>
        <Textarea value={urls} onChange={(e) => setUrls(e.target.value)} placeholder={t("hw_ph")} className="rounded-xl bg-white" data-testid="hwsmart-urls-input" />
        <button type="button" onClick={doImport} disabled={!!busy} className="h-11 inline-flex items-center gap-2 rounded-full bg-[#142B5C] px-5 text-sm font-bold text-white disabled:opacity-60" data-testid="hwsmart-import-btn">
          {busy === "import" && <Loader2 className="h-4 w-4 animate-spin" />}{t("import_btn")}
        </button>
      </div>
    </div>
  );
}

export default function ListingEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, pick } = useLang();
  const [f, setF] = useState(EMPTY);
  const [listing, setListing] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/extras").then((r) => setCatalog(r.data));
    if (id) api.get(`/my/listings/${id}`).then((r) => { setListing(r.data); setF({ ...EMPTY, ...r.data }); }).catch(() => nav("/dashboard"));
  }, [id, nav]);
  const set = (k, num) => (e) => setF({ ...f, [k]: num ? Number(e.target.value) : e.target.value });
  const toggleAm = (a) => setF({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] });
  const extraOf = (eid) => f.extras.find((x) => x.extra_id === eid);
  const toggleExtra = (c) => setF({ ...f, extras: extraOf(c.extra_id) ? f.extras.filter((x) => x.extra_id !== c.extra_id) : [...f.extras, { extra_id: c.extra_id, price: c.default_price }] });
  const setExtraPrice = (eid, price) => setF({ ...f, extras: f.extras.map((x) => (x.extra_id === eid ? { ...x, price: Number(price) } : x)) });

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const body = { ...f };
    ["listing_id", "owner_id", "photos", "status", "created_at", "updated_at", "price_month_public", "_id"].forEach((k) => delete body[k]);
    try {
      const { data } = id ? await api.put(`/my/listings/${id}`, body) : await api.post("/my/listings", body);
      toast.success(t("saved"));
      setListing(data);
      if (!id) nav(`/dashboard/listings/${data.listing_id}`, { replace: true });
      return data;
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const submitReview = async () => {
    const saved = await save();
    if (!saved) return;
    try { const { data } = await api.post(`/my/listings/${saved.listing_id}/submit`); setListing(data); toast.success(t("sent_review")); } catch (e) { toast.error(errMsg(e)); }
  };
  const fee = f.price_month * 0.05;
  const inp = "h-12 rounded-xl";
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="listing-editor">
      <button onClick={() => nav("/dashboard?tab=listings")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600" data-testid="editor-back-btn"><ArrowLeft className="h-4 w-4" />{t("tab_listings")}</button>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0B192C]">{id ? t("editor_edit") : t("editor_new")}</h1>
        {listing && <StatusBadge status={listing.status} testid="editor-status" />}
      </div>
      <form onSubmit={save} className="mt-8 space-y-5">
        <Card title={t("sec_info")}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("title_it")}><Input required value={f.title} onChange={set("title")} className={inp} data-testid="editor-title-input" /></Field>
            <Field label={t("title_en")}><Input value={f.title_en} onChange={set("title_en")} className={inp} data-testid="editor-title-en-input" /></Field>
            <Field label={t("city")}><Input required value={f.city} onChange={set("city")} className={inp} data-testid="editor-city-input" /></Field>
            <Field label={t("zone")}><Input value={f.zone} onChange={set("zone")} className={inp} data-testid="editor-zone-input" /></Field>
            <div className="sm:col-span-2"><Field label={t("address")}><Input value={f.address} onChange={set("address")} className={inp} data-testid="editor-address-input" /></Field></div>
            <Field label={t("type")}>
              <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
                <SelectTrigger className={inp} data-testid="editor-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`t_${ty}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("bedrooms")}><Input type="number" min="0" value={f.bedrooms} onChange={set("bedrooms", 1)} className={inp} data-testid="editor-bedrooms-input" /></Field>
              <Field label={t("bathrooms")}><Input type="number" min="0" value={f.bathrooms} onChange={set("bathrooms", 1)} className={inp} data-testid="editor-bathrooms-input" /></Field>
              <Field label={t("max_guests")}><Input type="number" min="1" value={f.max_guests} onChange={set("max_guests", 1)} className={inp} data-testid="editor-guests-input" /></Field>
              <Field label={t("size")}><Input type="number" min="0" value={f.size_sqm} onChange={set("size_sqm", 1)} className={inp} data-testid="editor-size-input" /></Field>
            </div>
            <div className="sm:col-span-2"><Field label={t("desc_it")}><Textarea rows={5} value={f.description} onChange={set("description")} className="rounded-xl" data-testid="editor-description-input" /></Field></div>
            <div className="sm:col-span-2"><Field label={t("desc_en")}><Textarea rows={3} value={f.description_en} onChange={set("description_en")} className="rounded-xl" data-testid="editor-description-en-input" /></Field></div>
          </div>
          <p className="mt-5 mb-3 text-sm font-semibold text-[#142B5C]">{t("amenities")}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {AMENITIES.map((a) => <label key={a} className="flex items-center gap-2 text-sm min-h-[40px]"><Checkbox checked={f.amenities.includes(a)} onCheckedChange={() => toggleAm(a)} data-testid={`editor-amenity-${a}`} />{t(`a_${a}`)}</label>)}
          </div>
        </Card>
        <Card title={t("sec_price")}>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("price_month")}><Input type="number" min="1" required value={f.price_month} onChange={set("price_month", 1)} className={inp} data-testid="editor-price-input" /></Field>
            <Field label={t("min_months")}><Input type="number" min="1" value={f.min_months} onChange={set("min_months", 1)} className={inp} data-testid="editor-min-months-input" /></Field>
            <Field label={t("available_from")}><Input type="date" value={f.available_from || ""} onChange={set("available_from")} className={inp} data-testid="editor-available-input" /></Field>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-xl bg-slate-50 p-4 text-sm" data-testid="editor-price-summary">
            <div><p className="text-xs text-slate-500">{t("your_price")}</p><p className="font-bold">{eur(f.price_month)}</p></div>
            <div><p className="text-xs text-slate-500">{t("public_price")}</p><p className="font-bold">{eur(f.price_month * 1.1)}</p></div>
            <div><p className="text-xs text-slate-500">{t("fee5")}</p><p className="font-bold text-red-600">-{eur(fee)}</p></div>
            <div><p className="text-xs text-slate-500">{t("net_month")}</p><p className="font-bold text-green-700" data-testid="editor-net">{eur(f.price_month - fee)}</p></div>
          </div>
        </Card>
        <Card title={t("sec_extras")}>
          <div className="grid gap-2" data-testid="editor-extras">
            {catalog.map((c) => {
              const sel = extraOf(c.extra_id);
              return (
                <div key={c.extra_id} className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 ${sel ? "border-[#FF6B00] bg-orange-50/50" : "border-slate-200"}`}>
                  <label className="flex items-center gap-2 font-semibold text-sm"><Checkbox checked={!!sel} onCheckedChange={() => toggleExtra(c)} data-testid={`editor-extra-${c.extra_id}`} />{pick(c, "name")} <span className="text-xs font-normal text-slate-500">({c.unit === "once" ? t("once") : t("monthly")})</span></label>
                  {sel && <div className="flex items-center gap-2 text-sm">€<Input type="number" min="0" value={sel.price} onChange={(e) => setExtraPrice(c.extra_id, e.target.value)} className="h-10 w-28 rounded-lg" data-testid={`editor-extra-price-${c.extra_id}`} /></div>}
                </div>
              );
            })}
          </div>
        </Card>
        <Card title={t("sec_photos")}>
          {listing ? <Photos listing={listing} setListing={setListing} /> : <p className="text-sm text-slate-500" data-testid="save-first-note">{t("save_first")}</p>}
        </Card>
        <div className="sticky bottom-0 -mx-4 sm:mx-0 flex gap-2 border-t sm:border sm:rounded-2xl border-slate-200 bg-white p-3">
          <button type="submit" disabled={busy} className="h-12 flex-1 rounded-full bg-[#142B5C] font-bold text-white disabled:opacity-60" data-testid="editor-save-btn">{t("save")}</button>
          {listing && listing.status === "draft" && (
            <button type="button" onClick={submitReview} disabled={busy || !listing.photos.length} title={!listing.photos.length ? t("photos_required") : ""} className="h-12 flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B00] font-bold text-white disabled:opacity-50" data-testid="editor-submit-review-btn"><Send className="h-4 w-4" />{t("submit_review")}</button>
          )}
        </div>
      </form>
    </div>
  );
}
